import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useReportLoadFailure } from "@/hooks/useReportLoadFailure";
import { analytics } from "@/lib/analytics";
import { handleActionError } from "@/lib/errors";
import { FavoriteHeart } from "@/components/favorites/FavoriteHeart";
import { useFavoritesListId } from "@/queries/customer/favorites";
import {
  useCreateSavedList,
  useSavedLists
} from "@/queries/customer/saved-lists";
import { toast } from "@medusajs/ui";
import { TruncateTooltip } from "@repo/design-system";
import { ChevronRight, ListPlus, Store } from "lucide-react";
import { memo, useCallback, useState } from "react";

import { SavedByLabel } from "./SavedByLabel";
import { SavedListNameDialog } from "./SavedListNameDialog";

function formatLastUsed(iso: string | null): string {
  if (!iso) return "Never used";
  const date = new Date(iso);
  return `Last used ${date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric"
  })}`;
}

interface SavedListsIndexViewProps {
  /** Open a list's detail view. */
  onSelect: (listId: string) => void;
}

/**
 * Saved Lists index (F3, #59) - cheap metadata cards (name, item/seller counts,
 * "Saved by", last used) with create. No per-line resolution here (the perf
 * boundary); live price/availability lives in the detail view.
 */
export const SavedListsIndexView = memo(function SavedListsIndexView({
  onSelect
}: SavedListsIndexViewProps) {
  const { data, isLoading, error } = useSavedLists();
  useReportLoadFailure(error, "saved lists");
  const createList = useCreateSavedList();
  const favoritesListId = useFavoritesListId();

  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreate = useCallback(
    async (name: string) => {
      setCreateError(null);
      try {
        const { saved_list } = await createList.mutateAsync({ name });
        analytics.savedListCreated({
          list_id: saved_list.id,
          source: "scratch",
          item_count: saved_list.items.length
        });
        setCreateOpen(false);
        toast.success(`"${saved_list.name}" created`);
        onSelect(saved_list.id);
      } catch (err) {
        setCreateError(
          handleActionError(err, {
            boundary: "saved list create",
            fallback: "We couldn't create your list. Please try again."
          })
        );
      }
    },
    [createList, onSelect]
  );

  const lists = data?.saved_lists ?? [];

  return (
    <div>
      <header className="border-border flex items-start justify-between gap-4 border-b p-6">
        <div>
          <h2
            className="text-xl font-semibold text-gray-900 dark:text-white"
            id="saved-lists-heading"
          >
            Saved Lists
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Reusable reorder lists for your salon
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
          <ListPlus className="mr-2 h-4 w-4" aria-hidden="true" />
          New list
        </Button>
      </header>

      <section aria-labelledby="saved-lists-heading" className="p-6">
        {isLoading ? (
          <ul className="space-y-3" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <li key={i} className="border-border rounded-lg border p-4">
                <Skeleton className="mb-2 h-5 w-40" />
                <Skeleton className="h-4 w-56" />
              </li>
            ))}
          </ul>
        ) : error ? (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-400"
          >
            We couldn&apos;t load your saved lists. Please try again.
          </div>
        ) : lists.length === 0 ? (
          <div
            className="flex min-h-[240px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 p-6 text-center dark:border-gray-700"
            role="status"
          >
            <Store
              className="mb-4 h-10 w-10 text-gray-400"
              aria-hidden="true"
            />
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              No saved lists yet
            </p>
            <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
              Save a cart as a list, or create one here to reorder the same
              products in a couple of clicks.
            </p>
            <Button
              type="button"
              className="mt-4"
              onClick={() => setCreateOpen(true)}
            >
              <ListPlus className="mr-2 h-4 w-4" aria-hidden="true" />
              New list
            </Button>
          </div>
        ) : (
          <ul className="space-y-3">
            {lists.map((list) => (
              <li key={list.id}>
                <button
                  type="button"
                  onClick={() => onSelect(list.id)}
                  className="border-border flex w-full items-center justify-between gap-4 rounded-lg border p-4 text-left transition-colors hover:border-purple-300 hover:bg-purple-50/50 focus:outline-none focus:ring-2 focus:ring-purple-600 dark:hover:bg-purple-900/10"
                >
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      {list.id === favoritesListId && (
                        <FavoriteHeart className="h-4 w-4" />
                      )}
                      <TruncateTooltip
                        as="p"
                        className="min-w-0 font-medium text-gray-900 dark:text-white"
                      >
                        {list.name}
                      </TruncateTooltip>
                    </div>
                    <TruncateTooltip
                      as="p"
                      className="mt-1 text-sm text-gray-500 dark:text-gray-400"
                    >
                      {list.item_count}{" "}
                      {list.item_count === 1 ? "item" : "items"}
                      {" · "}
                      {list.seller_count}{" "}
                      {list.seller_count === 1 ? "supplier" : "suppliers"}
                      {" · "}
                      <SavedByLabel label={list.created_by.label} />
                    </TruncateTooltip>
                    <p className="mt-0.5 text-xs text-gray-400">
                      {formatLastUsed(list.last_used_at)}
                    </p>
                  </div>
                  <ChevronRight
                    className="h-5 w-5 flex-shrink-0 text-gray-400"
                    aria-hidden="true"
                  />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <SavedListNameDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Create a saved list"
        description="Give your list a name. You can add products from the cart or product pages."
        submitLabel="Create list"
        isSubmitting={createList.isPending}
        error={createError}
        onSubmit={handleCreate}
      />
    </div>
  );
});
